import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

// Import components
import GroupPageShell from "../components/common/GroupPageShell";
import DivisionIntro from "../components/common/DivisionIntro";
import DivisionClients from "../components/common/DivisionClients";
import DivisionClientsStrip from "../components/common/DivisionClientsStrip";
import Contact from "../components/Hamdan Mining/MiningContact";
import MiningOperations from "../components/Hamdan Mining/MiningOperations";
import Products from "../components/Hamdan Mining/MiningProducts";
import Gallery from "../components/Hamdan Mining/MiningGallery";
import { buildClientLogoImages } from "../utils/clientLogos";
import DivisionServices from "../components/common/DivisionServices";

const miningProductCategories = ["Sand", "Aggregate", "Road Materials", "Rock & Filter"] as const;
type MiningProductCategory = typeof miningProductCategories[number];

const getMiningProductCategory = (category?: string): MiningProductCategory => {
  const normalizedCategory = category?.trim();
  return miningProductCategories.includes(normalizedCategory as MiningProductCategory)
    ? (normalizedCategory as MiningProductCategory)
    : "Aggregate";
};

const HamdanMining: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [content, setContent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t, t_api } = useLanguage();

  useEffect(() => {
    fetch('/api/homepage/content')
      .then(res => res.json())
      .then(data => {
        setContent(data.Mining || {});
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  // Get active tab from URL, default to "about"
  const activeTab = searchParams.get("tab") || "about";

  const menuItems = [
    { id: "about", label: t('common.about') },
    { id: "services", label: t('common.services') },
    { id: "gallery", label: t('common.gallery') },
    { id: "operation", label: t('common.operations') },
    { id: "products", label: t('common.projects') },
    { id: "clients", label: t('common.clients') },
    { id: "contact", label: t('common.contact') },
  ];

  // Derived content blocks
  const headerContent = useMemo(() => {
    const apiBanners = [1, 2, 3].map(i => ({
      image: content?.[`banner_${i}`]
    })).filter(b => b.image);

    return {
      title: t_api(content?.banner_title || "Hamdan Mining", content?.banner_title_ar),
      subtitle: t_api(content?.banner_subtitle || "Part of one of the largest corporate conglomerates from the sultanate of oman", content?.banner_subtitle_ar),
      banners: apiBanners.length ? apiBanners : [
        { image: "/banner/mining_1.jpg" },
        { image: "/banner/mining_2.jpg" },
        { image: "/banner/mining_3.jpg" },
      ]
    };
  }, [content, t_api]);

  const introContent = useMemo(() => ({
    kicker: "Hamdan Group - Oman",
    title: t_api(content?.about_title, content?.about_title_ar),
    desc: t_api(content?.about_desc, content?.about_desc_ar),
    image: content?.about_image || "/Hamdan-Mining-logo.jpg",
    imageFit: "contain",
    imageBadgeTitle: "Mining",
    imageLabel: t_api(content?.about_title, content?.about_title_ar) || "Hamdan Mining",
    noteTitle: "Website",
    noteDesc: content?.about_website || "www.hamdanmining.com",
    panelTitle: "INTRODUCTION",
    capabilitiesTitle: "Mining Capabilities",
    capabilitiesMeta: "Aggregates - HSE - Export",
    brandLogo: "/Hamdan-Mining-logo.jpg",
    brandDesc: `For more information please visit our website: ${content?.about_website || 'www.hamdanmining.com'}`,
    brandTags: ["Mining", "Aggregates", "Oman"],
    points: [1, 2, 3].map(i => ({
      title: t_api(content?.[`about_pt_${i}_title`], content?.[`about_pt_${i}_title_ar`]),
      desc: t_api(content?.[`about_pt_${i}_desc`], content?.[`about_pt_${i}_desc_ar`]),
      icon: content?.[`about_pt_${i}_icon`]
    })).filter(p => p.title)
  }), [content, t_api]);

  const operationsContent = useMemo(() => ({
    sections: [
      { id: 'prod', label: t('common.operations'), icon: 'Factory' },
      { id: 'hse', label: 'HSE & Quality', icon: 'ShieldCheck' },
      { id: 'cust', label: 'Customer Focus', icon: 'Handshake' },
      { id: 'exp', label: 'Export Markets', icon: 'Ship' }
    ].map(op => ({
      title: t_api(content?.[`${op.id}_title`] || op.label, content?.[`${op.id}_title_ar`]),
      desc: t_api(content?.[`${op.id}_desc`], content?.[`${op.id}_desc_ar`]),
      iconName: op.icon
    })).filter(o => o.desc)
  }), [content, t, t_api]);

  const productsContent = useMemo(() => ({
    items: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => {
      const category = getMiningProductCategory(content?.[`prod_${i}_cat`] || 'Aggregate');

      return {
        title: t_api(content?.[`prod_${i}_title`], content?.[`prod_${i}_title_ar`]),
        desc: t_api(content?.[`prod_${i}_desc`], content?.[`prod_${i}_desc_ar`]),
        category,
        iconName: category === 'Sand' ? 'Droplets' :
          category === 'Road Materials' ? 'Ruler' :
            category === 'Rock & Filter' ? 'Mountain' : 'Boxes'
      };
    }).filter(p => p.title)
  }), [content, t_api]);

  const galleryImages = useMemo(() => {
    return Object.entries(content || {})
      .filter(([k]) => k.startsWith('gallery_img_'))
      .map(([, v]) => v as string);
  }, [content]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">{t('common.loading')}</div>;

  const clientLogoImages = buildClientLogoImages(content);
  const clientLogoUrls = t_api(content?.clients_logo_urls, content?.clients_logo_urls_ar);

  return (
    <GroupPageShell
      heroContent={headerContent}
      tabs={menuItems}
      activeTab={activeTab}
      onTabChange={(tabId) => setSearchParams({ tab: tabId })}
    >
      <div className="mt-8">
        {activeTab === "about" && (
          <>
            <DivisionIntro content={introContent} />
            <DivisionClientsStrip
              title={t_api(content?.clients_title, content?.clients_title_ar)}
              desc={t_api(content?.clients_desc, content?.clients_desc_ar)}
              clientsText={t_api(content?.clients_list, content?.clients_list_ar)}
              logoUrls={clientLogoUrls}
              logoImages={clientLogoImages}
            />
          </>
        )}
        {activeTab === "services" && (
          <DivisionServices content={content} />
        )}
        {activeTab === "gallery" && <Gallery content={{ images: galleryImages, title: t_api(content?.gallery_title, content?.gallery_title_ar), subtitle: t_api(content?.gallery_subtitle, content?.gallery_subtitle_ar) }} />}
        {activeTab === "operation" && <MiningOperations content={operationsContent} />}
        {activeTab === "products" && <Products content={productsContent} />}
        {activeTab === "clients" && <DivisionClients content={{
          title: t_api(content?.clients_title, content?.clients_title_ar),
          desc: t_api(content?.clients_desc, content?.clients_desc_ar),
          clientsText: t_api(content?.clients_list, content?.clients_list_ar),
          logoUrls: clientLogoUrls,
          logoImages: clientLogoImages,
          divisionLabel: "Hamdan Mining",
          footer: t_api(content?.clients_footer, content?.clients_footer_ar),
          footerDesc: t_api(content?.clients_footer_desc, content?.clients_footer_desc_ar),
        }} />}
        {activeTab === "contact" && (
          <Contact content={{
            formTitle: t_api(content?.contact_form_title, content?.contact_form_title_ar),
            infoTitle: t_api(content?.contact_info_title, content?.contact_info_title_ar),
            infoDesc: t_api(content?.contact_info_desc, content?.contact_info_desc_ar),
            address: t_api(content?.contact_address, content?.contact_address_ar),
            phone: content?.contact_phone,
            fax: content?.contact_fax,
            email: content?.contact_email
          }} />
        )}
      </div>
    </GroupPageShell>
  );
};

export default HamdanMining;
